// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: shared pieces of the phone's swipe-from-the-left-edge back gesture
// (EdgeSwipeBack). A screen opts in by putting `data-swipe-back` on its header
// back button; the gesture presses the topmost visible one. It slides the
// closest `data-swipe-back-scope` ancestor (a full-screen modal) or, failing
// that, the main page column. Other sideways gestures skip touches that start
// in the edge strip while the gesture is on, so the two never fight.

/** Width of the strip along the left edge where a touch can start a back swipe. */
export const SWIPE_BACK_EDGE = 24;

let enabled = false;

export function setSwipeBackEnabled(value: boolean): void {
	enabled = value;
}

/** True when a touch starting at `clientX` belongs to the back swipe, not to the element under it. */
export function swipeBackOwnsTouch(clientX: number): boolean {
	return enabled && clientX < SWIPE_BACK_EDGE;
}

function isOnTop(element: HTMLElement): boolean {
	const rect = element.getBoundingClientRect();
	if (rect.width === 0 || rect.height === 0) return false;
	if (rect.right <= 0 || rect.bottom <= 0 || rect.left >= window.innerWidth || rect.top >= window.innerHeight) {
		return false;
	}
	const hit = document.elementFromPoint(rect.left + rect.width / 2, rect.top + rect.height / 2);
	return hit != null && (hit === element || element.contains(hit));
}

/** The back button of the screen the user is looking at: visible, enabled and not covered by a sheet or modal. */
export function findSwipeBackButton(): HTMLElement | null {
	const buttons = document.querySelectorAll<HTMLElement>('[data-swipe-back]');
	for (let index = buttons.length - 1; index >= 0; index--) {
		const button = buttons[index];
		if (button instanceof HTMLButtonElement && button.disabled) continue;
		if (isOnTop(button)) return button;
	}
	return null;
}

/** The element that follows the finger for this back button. */
export function swipeBackScope(button: HTMLElement): HTMLElement | null {
	const scope = button.closest<HTMLElement>('[data-swipe-back-scope]') ?? document.getElementById('main-content');
	return scope?.contains(button) ? scope : null;
}
